import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  AppBar,
  Toolbar,
  Alert,
  CircularProgress,
  Slider,
  IconButton,
  Tooltip
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CodeIcon from '@mui/icons-material/Code';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import SettingsIcon from '@mui/icons-material/Settings';
import { FormCanvas } from '../components/FormEditor/FormCanvas';
import { FieldPropertiesPanel } from '../components/FormEditor/FieldPropertiesPanel';
import { FieldListPanel } from '../components/FormEditor/FieldListPanel';
import { SqlGeneratorDialog } from '../components/FormEditor/SqlGeneratorDialog';
import { HeaderConfigDialog } from '../components/FormEditor/HeaderConfigDialog';
import { AutoLayoutSettingsDialog, DEFAULT_AUTO_LAYOUT_SETTINGS, AutoLayoutSettings } from '../components/FormEditor/AutoLayoutSettingsDialog';
import { formEditorService } from '../services/formEditorService';
import { formService } from '../services/formService';
import {
  FormEditorDto,
  FormFieldEditorDto,
  FormSectionEditorDto,
  PdfFieldConfig,
  SectionPdfConfig,
  CreateFormFieldEditorDto,
  FormFieldType
} from '../types/formEditor';

export const FormEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [forms, setForms] = useState<FormEditorDto[]>([]);
  const [selectedFormId, setSelectedFormId] = useState<number | null>(null);
  const [selectedForm, setSelectedForm] = useState<FormEditorDto | null>(null);
  const [selectedFieldId, setSelectedFieldId] = useState<number | undefined>();
  const [selectedSectionId, setSelectedSectionId] = useState<number | undefined>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [scale, setScale] = useState<number>(0.75);
  const [sqlDialogOpen, setSqlDialogOpen] = useState<boolean>(false);
  const [headerDialogOpen, setHeaderDialogOpen] = useState<boolean>(false);
  const [autoLayoutSettingsOpen, setAutoLayoutSettingsOpen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [autoLayoutSettings, setAutoLayoutSettings] = useState<AutoLayoutSettings>(DEFAULT_AUTO_LAYOUT_SETTINGS);

  const getDefaultSectionPdfConfig = (pageNumber: number = 1): SectionPdfConfig => ({
    x: 50,
    y: 100,
    width: 495,
    height: 200,
    borderColor: '#cccccc',
    borderWidth: 1,
    showBorder: true,
    pageNumber
  });

  // Check if user is admin
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!user?.isAdmin) {
      navigate('/');
      return;
    }

    loadForms();
  }, [navigate, isAuthenticated, user]);

  const loadForms = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await formEditorService.getForms();
      setForms(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const loadForm = async (formId: number) => {
    setLoading(true);
    setError('');
    setSelectedFieldId(undefined);
    try {
      const data = await formEditorService.getForm(formId);
      setSelectedForm(data);
      setSelectedFormId(formId);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load form');
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (formId: number) => {
    loadForm(formId);
  };

  const getAllFields = (): FormFieldEditorDto[] => {
    if (!selectedForm) return [];
    return selectedForm.formSections.flatMap((section) => section.formFields);
  };

  const getFieldsForCurrentPage = (): FormFieldEditorDto[] => {
    const allFields = getAllFields();
    const filteredFields = allFields.filter(field => {
      const pageNum = field.pdfConfig?.pageNumber || 1;
      const matches = pageNum === currentPage;
      if (!matches && field.code === 'CLIENT_NAME') {
        console.log('CLIENT_NAME filtered out:', {
          fieldId: field.id,
          configuredPage: pageNum,
          currentPage: currentPage,
          pdfConfig: field.pdfConfig
        });
      }
      return matches;
    });

    console.log(`Page ${currentPage}: Showing ${filteredFields.length} of ${allFields.length} total fields`);
    return filteredFields;
  };

  const getTotalPages = (): number => {
    const allFields = getAllFields();
    if (allFields.length === 0) return 1;
    const maxPage = Math.max(...allFields.map(field => field.pdfConfig?.pageNumber || 1));
    return Math.max(maxPage, currentPage);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setSelectedFieldId(undefined); // Clear selection when changing pages
    }
  };

  const handleNextPage = () => {
    const totalPages = getTotalPages();
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setSelectedFieldId(undefined);
    }
  };

  const handleAddPage = () => {
    setCurrentPage(getTotalPages() + 1);
    setSelectedFieldId(undefined);
  };

  const getSelectedField = (): FormFieldEditorDto | undefined => {
    if (!selectedFieldId) return undefined;
    return getAllFields().find((f) => f.id === selectedFieldId);
  };

  const getSelectedSection = (): FormSectionEditorDto | undefined => {
    if (!selectedSectionId || !selectedForm) return undefined;
    return selectedForm.formSections.find((s) => s.id === selectedSectionId);
  };

  const handleSectionSelect = (sectionId: number) => {
    setSelectedSectionId(sectionId);
    setSelectedFieldId(undefined); // Clear field selection when selecting section
  };

  const handleFieldSelect = (fieldId: number) => {
    setSelectedFieldId(fieldId);
    setSelectedSectionId(undefined); // Clear section selection when selecting field
  };

  const handleSectionUpdate = async (sectionId: number, pdfConfig: SectionPdfConfig) => {
    if (!selectedForm) return;

    try {
      // Update section via API
      const section = selectedForm.formSections.find(s => s.id === sectionId);
      if (!section) return;

      const updatedSection = await formEditorService.updateSection(sectionId, {
        code: section.code,
        description: section.description,
        displayOrder: section.displayOrder,
        columnNumber: section.columnNumber,
        isActive: section.isActive,
        configurationJson: section.configurationJson,
        pdfConfig: pdfConfig
      });

      // Update local state with the result from the server
      const updatedForm = { ...selectedForm };
      updatedForm.formSections = updatedForm.formSections.map((s) =>
        s.id === sectionId ? updatedSection : s
      );
      setSelectedForm(updatedForm);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update section');
    }
  };

  const handleFieldUpdate = async (field: FormFieldEditorDto) => {
    // Optimistically update local state first for immediate visual feedback
    if (selectedForm) {
      const updatedForm = { ...selectedForm };
      updatedForm.formSections = updatedForm.formSections.map((section) => ({
        ...section,
        formFields: section.formFields.map((f) => (f.id === field.id ? field : f))
      }));
      setSelectedForm(updatedForm);
    }

    // Then persist to server in background
    try {
      const updatedFieldResult = await formEditorService.updateField(field.id, {
        code: field.code,
        description: field.description,
        displayOrder: field.displayOrder,
        columnNumber: field.columnNumber,
        fieldType: field.fieldType,
        formFieldDataTypeId: field.formFieldDataTypeId,
        info: field.info,
        isRequired: field.isRequired,
        isActive: field.isActive,
        configurationJson: field.configurationJson,
        pdfConfig: field.pdfConfig
      });

      // Update state with actual server result to ensure configurationJson (which might have been merged) 
      // is correctly synced from the response
      if (selectedForm && updatedFieldResult) {
        const updatedForm = { ...selectedForm };
        updatedForm.formSections = updatedForm.formSections.map((section) => ({
          ...section,
          formFields: section.formFields.map((f) => (f.id === field.id ? updatedFieldResult : f))
        }));
        setSelectedForm(updatedForm);
      }
    } catch (err: any) {
      console.error('Failed to update field:', err);
      setError(err.response?.data?.message || 'Failed to update field');
      // Reload form on error to revert optimistic update
      if (selectedForm) {
        await loadForm(selectedForm.id);
      }
    }
  };

  const handlePdfConfigUpdate = async (fieldId: number, pdfConfig: PdfFieldConfig) => {
    const field = getAllFields().find((f) => f.id === fieldId);
    if (field) {
      const updatedField = { ...field, pdfConfig };
      await handleFieldUpdate(updatedField);
    }
  };

  const handleAddField = async () => {
    if (!selectedForm || selectedForm.formSections.length === 0) {
      setError('No sections available to add field to');
      return;
    }

    const firstSection = selectedForm.formSections[0];
    const pageFields = getFieldsForCurrentPage();
    const maxDisplayOrder = Math.max(
      0,
      ...pageFields.map((f) => f.displayOrder)
    );

    // Calculate default position based on number of existing fields
    const defaultX = 50 + (pageFields.length * 10); // Spread out horizontally
    const defaultY = 150 + (pageFields.length * 30); // Stack vertically

    const newField: CreateFormFieldEditorDto = {
      formSectionId: firstSection.id,
      code: `field_${Date.now()}`,
      description: 'New Field',
      displayOrder: maxDisplayOrder + 1,
      columnNumber: 1,
      fieldType: FormFieldType.String,
      isRequired: false,
      isActive: true,
      pdfConfig: {
        x: defaultX,
        y: defaultY,
        width: 150,
        height: 25,
        fontFamily: 'Arial',
        fontSize: 12,
        fontColor: '#000000',
        tabOrder: 0,
        alignment: 'Left',
        isMultiline: false,
        isBold: false,
        isItalic: false,
        pageWidth: 595,
        pageHeight: 842,
        pageNumber: currentPage,
        labelConfig: {
          x: defaultX,
          y: defaultY, // Same Y-axis as field
          width: 150,
          height: 25,
          fontFamily: 'Arial',
          fontSize: 10,
          fontColor: '#000000',
          isBold: false,
          isItalic: false,
          alignment: 'Left',
          verticalAlignment: 'Top',
          isVisible: true
        }
      }
    };

    try {
      const createdField = await formEditorService.createField(newField);

      // Update local state
      if (selectedForm) {
        const updatedForm = { ...selectedForm };
        updatedForm.formSections = updatedForm.formSections.map((section) =>
          section.id === firstSection.id
            ? { ...section, formFields: [...section.formFields, createdField] }
            : section
        );
        setSelectedForm(updatedForm);
        setSelectedFieldId(createdField.id);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create field');
    }
  };

  const handleDeleteField = async (fieldId: number) => {
    if (!confirm('Are you sure you want to delete this field?')) return;

    try {
      await formEditorService.deleteField(fieldId);

      // Update local state
      if (selectedForm) {
        const updatedForm = { ...selectedForm };
        updatedForm.formSections = updatedForm.formSections.map((section) => ({
          ...section,
          formFields: section.formFields.filter((f) => f.id !== fieldId)
        }));
        setSelectedForm(updatedForm);
        setSelectedFieldId(undefined);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete field');
    }
  };

  const handleSaveHeaderConfig = async (headerConfigJson: string) => {
    if (!selectedForm) return;

    try {
      // Update the form with new header configuration
      const updateDto = {
        code: selectedForm.code,
        description: selectedForm.description,
        displayOrder: selectedForm.displayOrder,
        numberOfColumns: selectedForm.numberOfColumns,
        pdfConfigurationJson: selectedForm.pdfConfigurationJson,
        headerConfigurationJson: headerConfigJson,
        isActive: selectedForm.isActive,
        isPrintable: selectedForm.isPrintable
      };

      const updatedForm = await (formService as any).updateForm(selectedForm.id, updateDto);
      setSelectedForm(updatedForm);
      console.log('Header configuration saved successfully!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save header configuration');
    }
  };

  const handleAutoLayout = async () => {
    try {
      console.log('Auto layout clicked');
      if (!selectedForm) {
        console.log('No selected form');
        return;
      }

      const pageFields = getFieldsForCurrentPage();
      console.log('Page fields:', pageFields);
      if (pageFields.length === 0) {
        console.log('No fields on current page');
        return;
      }

      // Sort sections by display order
      const sortedSections = [...selectedForm.formSections]
        .sort((a, b) => a.id - b.id); // Sort by FormSectionId

      // Collect all fields in the correct order: section order -> display order -> column number
      const sortedFields: FormFieldEditorDto[] = [];
      sortedSections.forEach(section => {
        const sectionFields = section.formFields
          .filter(field => (field.pdfConfig?.pageNumber || 1) === currentPage) // Same logic as getFieldsForCurrentPage
          .sort((a, b) => {
            // First sort by display order
            const displayDiff = a.displayOrder - b.displayOrder;
            if (displayDiff !== 0) return displayDiff;

            // Then sort by column number
            return a.columnNumber - b.columnNumber;
          });

        sortedFields.push(...sectionFields);
      });

      // Calculate the number of columns needed (ColumnNumber 0 = full width, others create columns)
      const maxNonZeroColumn = Math.max(...sortedFields
        .map(field => field.columnNumber)
        .filter(col => col > 0), 0);
      const columnCount = maxNonZeroColumn > 0 ? maxNonZeroColumn : 1;
      const pageWidth = 595; // A4 width in points
      const pageHeight = 842; // A4 height in points
      const margin = 0; // Margin from edges
      const fieldSpacing = autoLayoutSettings.verticalSpacing; // Vertical spacing between fields
      const columnSpacing = autoLayoutSettings.horizontalSpacing; // Horizontal spacing between columns
      const sectionSpacing = autoLayoutSettings.sectionSpacing; // Spacing between sections

      const availableWidth = pageWidth - (margin * 2) - (columnSpacing * (columnCount - 1));
      const columnWidth = availableWidth / columnCount;
      console.log('Column width:', columnWidth);
      console.log('Available width:', availableWidth);
      console.log('Column count:', columnCount);
      console.log('Column spacing:', columnSpacing);
      console.log('Margin:', margin);
      console.log('Field spacing:', fieldSpacing);
      console.log('Page width:', pageWidth);
      console.log('Page height:', pageHeight);

      // Calculate header height to position fields below header
      let headerHeight = 0;
      if (selectedForm?.headerConfigurationJson) {
        try {
          const headerConfig = JSON.parse(selectedForm.headerConfigurationJson);
          headerHeight = headerConfig.height || 0;
        } catch (error) {
          console.warn('Failed to parse header config for height calculation:', error);
        }
      }

      // Calculate new positions, iterating through sections to maintain vertical separation
      const fieldUpdates: Array<{ field: FormFieldEditorDto, newConfig: PdfFieldConfig }> = [];

      console.log('Auto Layout - Header Height:', headerHeight);
      console.log('Auto Layout - Margin:', margin);
      console.log('Auto Layout - Field Spacing:', fieldSpacing);
      console.log('Auto Layout - Section Spacing:', sectionSpacing);
      console.log('Auto Layout - First field should start at Y:', margin + headerHeight + sectionSpacing);

      // Track Y positions independently for each column
      // columnYPositions[0] = full width sections, [1] = left column, [2] = right column
      const columnYPositions: Record<number, number> = {
        0: margin + headerHeight + sectionSpacing, // Full width starts below header with spacing
        1: margin + headerHeight + sectionSpacing, // Left column starts below header with spacing
        2: margin + headerHeight + sectionSpacing  // Right column starts below header with spacing
      };

      // Sort sections by DisplayOrder
      const sortedSectionsForLayout = [...sortedSections]
        .filter(section => section.formFields.some(field => (field.pdfConfig?.pageNumber || 1) === currentPage))
        .sort((a, b) => a.displayOrder - b.displayOrder);

      console.log('Column Y Positions (initial):', columnYPositions);

      // Process each section in order
      sortedSectionsForLayout.forEach(section => {
        const sectionFields = section.formFields
          .filter(field => (field.pdfConfig?.pageNumber || 1) === currentPage)
          .sort((a, b) => a.columnNumber - b.columnNumber);

        if (sectionFields.length === 0) return;

        // Determine which column this section belongs to
        const sectionColumn = section.columnNumber; // 0 = full width, 1 = left, 2 = right

        // For full-width sections, they start at the max Y of all columns
        let fieldY: number;
        if (sectionColumn === 0) {
          fieldY = Math.max(columnYPositions[0], columnYPositions[1], columnYPositions[2]);
        } else {
          fieldY = columnYPositions[sectionColumn];
        }

        console.log(`Section (${section.description}, DisplayOrder: ${section.displayOrder}, Column: ${sectionColumn}): Starting at Y=${fieldY}`);

        // Check if this section has fields with ColumnNumber = 2 (indicating sub-columns)
        const hasSubColumns = sectionFields.some(field => field.columnNumber === 2);
        // Use narrower labels for sub-columns, otherwise use settings default
        const labelWidth = (section.columnNumber > 0 && hasSubColumns) ? 40 : autoLayoutSettings.labelWidth;

        // Create rows: fields with same DisplayOrder OR consecutive DisplayOrder (col1->col2) go on same row
        const sortedSectionFields = [...sectionFields].sort((a, b) => {
          // Sort by DisplayOrder first, then by ColumnNumber
          const displayDiff = a.displayOrder - b.displayOrder;
          if (displayDiff !== 0) return displayDiff;
          return a.columnNumber - b.columnNumber;
        });

        const fieldRows: FormFieldEditorDto[][] = [];
        let i = 0;

        while (i < sortedSectionFields.length) {
          const currentField = sortedSectionFields[i];
          const nextField = sortedSectionFields[i + 1];

          // Check if we can pair this field with the next one
          if (nextField && currentField.columnNumber === 1 && nextField.columnNumber === 2) {
            // Same DisplayOrder - definitely pair them
            if (nextField.displayOrder === currentField.displayOrder) {
              fieldRows.push([currentField, nextField]);
              i += 2;
            }
            // Consecutive DisplayOrder - also pair them (e.g., 3 and 4)
            else if (nextField.displayOrder === currentField.displayOrder + 1) {
              fieldRows.push([currentField, nextField]);
              i += 2;
            }
            // Different DisplayOrder, not consecutive - separate rows
            else {
              fieldRows.push([currentField]);
              i += 1;
            }
          } else {
            // Single field on this row
            fieldRows.push([currentField]);
            i += 1;
          }
        }

        let currentRowY = fieldY;

        // Process each row
        fieldRows.forEach(fieldsInRow => {
          // Calculate Y for this field row within the section
          const sectionFieldY = currentRowY;

          // Determine the max height for this row (for multiline fields)
          const maxRowHeight = Math.max(...fieldsInRow.map(f => {
            const isMultiline = f.fieldType === 2; // FormFieldType.Multiline = 2
            return isMultiline ? 50 : 25;
          }));

          fieldsInRow.forEach(field => {
            let labelX: number;
            let fieldX: number;
            let fieldWidth: number;
            const labelFieldGap = autoLayoutSettings.labelFieldGap;

            // Determine the effective column constraints
            // If section has ColumnNumber > 0, fields are positioned relative to section boundaries
            // If section has ColumnNumber = 0, fields are positioned relative to page boundaries

            let effectiveColumnIndex: number;
            let effectiveColumnWidth: number;
            let effectiveColumnSpacing: number;

            if (section.columnNumber === 0) {
              // Section is full width - use page-level column layout
              if (field.columnNumber === 0) {
                // Field wants full width
                effectiveColumnIndex = 0;
                effectiveColumnWidth = availableWidth;
              } else {
                // Field has specific column
                effectiveColumnIndex = field.columnNumber - 1;
                effectiveColumnWidth = columnWidth;
              }
              effectiveColumnSpacing = columnSpacing;
              const columnStartX = margin + (effectiveColumnIndex * (effectiveColumnWidth + effectiveColumnSpacing));
              labelX = columnStartX + 5;
              fieldX = labelX + labelWidth + labelFieldGap;
              fieldWidth = effectiveColumnWidth - labelWidth - labelFieldGap - 15;
            } else {
              // Section is constrained to a column - create sub-columns within the section
              const sectionColumnIndex = section.columnNumber - 1;
              const sectionStartX = margin + (sectionColumnIndex * (columnWidth + columnSpacing));

              if (field.columnNumber === 0) {
                // Field wants full section width
                labelX = sectionStartX + 5;
                fieldX = labelX + labelWidth + labelFieldGap;
                fieldWidth = columnWidth - labelWidth - labelFieldGap - 15;
              } else {
                // Field has specific column within the section
                // Create 2 sub-columns within the section
                // Account for section padding (5px on each side) and spacing between sub-columns
                const sectionAvailableWidth = columnWidth - 10; // 5px padding on each side
                const subColumnWidth = (sectionAvailableWidth - columnSpacing) / 2; // Split available width minus spacing
                const fieldSubColumnIndex = field.columnNumber - 1; // 0 for left sub-column, 1 for right sub-column
                const subColumnStartX = sectionStartX + 5 + (fieldSubColumnIndex * (subColumnWidth + columnSpacing));

                labelX = subColumnStartX;
                fieldX = labelX + labelWidth + labelFieldGap;
                fieldWidth = subColumnWidth - labelWidth - labelFieldGap;
              }
            }

            const pdfConfig = field.pdfConfig || {
              x: 0,
              y: 0,
              width: Math.max(120, fieldWidth),
              height: 25,
              fontFamily: 'Arial',
              fontSize: autoLayoutSettings.fontSize,
              fontColor: '#000000',
              tabOrder: 0,
              alignment: 'Left',
              isMultiline: false,
              isBold: false,
              isItalic: false,
              pageWidth: 595,
              pageHeight: 842,
              pageNumber: currentPage
            };

            // Set height based on field type - multiline fields get 50px height
            const isMultiline = field.fieldType === 2; // FormFieldType.Multiline = 2
            const fieldHeight = isMultiline ? 50 : (field.pdfConfig?.height || 25);

            const updatedConfig = {
              ...pdfConfig,
              x: Math.round(fieldX),
              y: Math.round(sectionFieldY),
              width: Math.round(Math.max(120, fieldWidth)),
              height: fieldHeight,
              fontSize: autoLayoutSettings.fontSize,
              labelConfig: {
                x: Math.round(labelX),
                y: Math.round(sectionFieldY), // Same Y-axis as the field
                width: labelWidth,
                height: fieldHeight,
                fontFamily: 'Arial',
                fontSize: Math.max(8, autoLayoutSettings.fontSize - 2), // Label font slightly smaller
                fontColor: '#000000',
                isBold: false,
                isItalic: false,
                alignment: 'Left',
                verticalAlignment: 'Top',
                isVisible: true
              }
            };

            fieldUpdates.push({ field, newConfig: updatedConfig });
          });

          // Move to next row
          currentRowY += maxRowHeight + fieldSpacing;
        });

        // Calculate the bottom Y of this section
        const sectionBottomY = currentRowY + sectionSpacing; // Add spacing after section

        // Update the column Y position(s)
        if (sectionColumn === 0) {
          // Full-width section updates all column Y positions
          columnYPositions[0] = sectionBottomY;
          columnYPositions[1] = sectionBottomY;
          columnYPositions[2] = sectionBottomY;
        } else {
          // Column-specific section only updates that column's Y position
          columnYPositions[sectionColumn] = sectionBottomY;
        }

        console.log(`  Section ended at Y=${sectionBottomY}. Column Y Positions:`, columnYPositions);
      });

      // Optimistically update local state immediately for instant visual feedback
      if (selectedForm) {
        const updatedForm = { ...selectedForm };
        updatedForm.formSections = updatedForm.formSections.map((section) => ({
          ...section,
          formFields: section.formFields.map((f) => {
            const update = fieldUpdates.find(u => u.field.id === f.id);
            return update ? { ...f, pdfConfig: update.newConfig } : f;
          })
        }));
        setSelectedForm(updatedForm);
      }

      // Update all fields via API in background
      const updatePromises = fieldUpdates.map(({ field, newConfig }) =>
        formEditorService.updateField(field.id, {
          code: field.code,
          description: field.description,
          displayOrder: field.displayOrder,
          columnNumber: field.columnNumber,
          fieldType: field.fieldType,
          formFieldDataTypeId: field.formFieldDataTypeId,
          info: field.info,
          isRequired: field.isRequired,
          isActive: field.isActive,
          configurationJson: field.configurationJson,
          pdfConfig: newConfig
        })
      );

      console.log(`Updating ${updatePromises.length} fields...`);
      await Promise.all(updatePromises);
      console.log('Auto layout fields updated successfully!');

      // Now position sections based on their fields
      const sectionUpdates: Array<{ section: FormSectionEditorDto, newConfig: SectionPdfConfig }> = [];

      // Group sections by DisplayOrder again for positioning
      const sectionsByDisplayOrderForPositioning = sortedSections.reduce((acc, section) => {
        const key = section.displayOrder;
        if (!acc[key]) acc[key] = [];
        acc[key].push(section);
        return acc;
      }, {} as Record<number, FormSectionEditorDto[]>);

      let displayOrderIndex = 0;
      Object.keys(sectionsByDisplayOrderForPositioning)
        .sort((a, b) => parseInt(a) - parseInt(b))
        .forEach(displayOrderStr => {
          const displayOrderSections = sectionsByDisplayOrderForPositioning[parseInt(displayOrderStr)];

          displayOrderSections.forEach(section => {
            const sectionFields = section.formFields
              .filter(field => (field.pdfConfig?.pageNumber || 1) === currentPage);

            if (sectionFields.length === 0) return;

            // Calculate section boundaries from its fields
            const sectionBounds = sectionFields.reduce(
              (bounds, field) => {
                const config = fieldUpdates.find(u => u.field.id === field.id)?.newConfig || field.pdfConfig;
                if (!config) return bounds;

                return {
                  minX: Math.min(bounds.minX, config.labelConfig?.x || config.x),
                  minY: Math.min(bounds.minY, config.y),
                  maxX: Math.max(bounds.maxX, (config.labelConfig?.x || config.x) + (config.labelConfig?.width || config.width)),
                  maxY: Math.max(bounds.maxY, config.y + config.height)
                };
              },
              { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity }
            );

            if (sectionBounds.minX === Infinity) return;

            let sectionX: number;
            let sectionWidth: number;

            if (section.columnNumber === 0) {
              // Full width section with configurable margin on each side
              sectionX = margin + autoLayoutSettings.sectionMargin;
              sectionWidth = pageWidth - (margin * 2) - (autoLayoutSettings.sectionMargin * 2);
            } else {
              // Column-based section with configurable gap on each side
              const columnIndex = section.columnNumber - 1;
              const gapPerSide = autoLayoutSettings.sectionMargin / 2; // Split the margin between sides
              sectionX = margin + (columnIndex * (columnWidth + columnSpacing)) + gapPerSide;
              sectionWidth = columnWidth - autoLayoutSettings.sectionMargin;
            }

            const sectionY = sectionBounds.minY - 5; // Start just before first field
            const sectionHeight = sectionBounds.maxY - sectionBounds.minY + 10; // Cover all fields with padding

            const sectionPdfConfig = section.pdfConfig || getDefaultSectionPdfConfig(currentPage);
            const updatedSectionConfig = {
              ...sectionPdfConfig,
              x: Math.round(sectionX),
              y: Math.round(sectionY),
              width: Math.round(sectionWidth),
              height: Math.round(sectionHeight),
              pageNumber: currentPage
            };

            sectionUpdates.push({ section, newConfig: updatedSectionConfig });
          });

          displayOrderIndex++;
        });

      // Optimistically update sections in local state
      if (sectionUpdates.length > 0 && selectedForm) {
        const updatedForm = { ...selectedForm };
        updatedForm.formSections = updatedForm.formSections.map((s) => {
          const update = sectionUpdates.find(u => u.section.id === s.id);
          return update ? { ...s, pdfConfig: update.newConfig } : s;
        });
        setSelectedForm(updatedForm);
      }

      // Update sections via API in background
      if (sectionUpdates.length > 0) {
        console.log(`Updating ${sectionUpdates.length} sections...`);
        const sectionUpdatePromises = sectionUpdates.map(({ section, newConfig }) =>
          formEditorService.updateSection(section.id, {
            code: section.code,
            description: section.description,
            displayOrder: section.displayOrder,
            columnNumber: section.columnNumber,
            isActive: section.isActive,
            pdfConfig: newConfig
          })
        );

        await Promise.all(sectionUpdatePromises);
        console.log('Section auto layout completed successfully!');
      }

      console.log('Auto layout completed!');
    } catch (err: any) {
      console.error('Auto layout error:', err);
      setError(err.response?.data?.message || 'Failed to auto-layout fields');
    }
  };

  if (loading && !selectedForm) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }


  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Header */}
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            PDF Form Editor
          </Typography>

          {selectedForm && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
              <IconButton
                size="small"
                onClick={handlePreviousPage}
                disabled={currentPage <= 1}
              >
                <NavigateBeforeIcon />
              </IconButton>

              <Typography variant="body2" sx={{ minWidth: 80, textAlign: 'center' }}>
                Page {currentPage} of {getTotalPages()}
              </Typography>

              <IconButton
                size="small"
                onClick={handleNextPage}
                disabled={currentPage >= getTotalPages()}
              >
                <NavigateNextIcon />
              </IconButton>

              <IconButton
                size="small"
                onClick={handleAddPage}
                color="primary"
                title="Add New Page"
              >
                <AddIcon />
              </IconButton>
            </Box>
          )}


          <FormControl sx={{ minWidth: 300, mr: 2 }} size="small">
            <InputLabel>Select Form</InputLabel>
            <Select
              value={selectedFormId || ''}
              label="Select Form"
              onChange={(e) => handleFormChange(Number(e.target.value))}
              displayEmpty
            >
              <MenuItem value="" disabled>
                <em>Select a form...</em>
              </MenuItem>
              {forms.map((form) => (
                <MenuItem key={form.id} value={form.id}>
                  {form.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedForm && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
                <Tooltip title="Zoom Out">
                  <IconButton
                    size="small"
                    onClick={() => setScale(Math.max(0.25, scale - 0.25))}
                  >
                    <ZoomOutIcon />
                  </IconButton>
                </Tooltip>
                <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'center' }}>
                  {Math.round(scale * 100)}%
                </Typography>
                <Tooltip title="Zoom In">
                  <IconButton
                    size="small"
                    onClick={() => setScale(Math.min(2, scale + 0.25))}
                  >
                    <ZoomInIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<AutoFixHighIcon />}
                  onClick={handleAutoLayout}
                >
                  Auto Layout
                </Button>
                <Tooltip title="Auto Layout Settings">
                  <IconButton
                    size="small"
                    onClick={() => setAutoLayoutSettingsOpen(true)}
                  >
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Button
                variant="outlined"
                startIcon={<InfoIcon />}
                onClick={() => setHeaderDialogOpen(true)}
                sx={{ mr: 1 }}
              >
                Header Config
              </Button>

              <Button
                variant="contained"
                startIcon={<CodeIcon />}
                onClick={() => setSqlDialogOpen(true)}
              >
                Generate SQL
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {/* Main Content */}
      {selectedForm ? (
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left: Canvas */}
          <Box sx={{ flex: 2, overflow: 'hidden' }}>
            <FormCanvas
              form={selectedForm}
              fields={getFieldsForCurrentPage()}
              selectedFieldId={selectedFieldId}
              selectedSectionId={selectedSectionId}
              onFieldSelect={handleFieldSelect}
              onSectionSelect={handleSectionSelect}
              onFieldUpdate={handlePdfConfigUpdate}
              onSectionUpdate={handleSectionUpdate}
              scale={scale}
              currentPage={currentPage}
              headerConfig={selectedForm?.headerConfigurationJson}
              autoLayoutSettings={autoLayoutSettings}
            />
          </Box>

          {/* Right: Properties and Field List */}
          <Box
            sx={{
              width: 400,
              display: 'flex',
              flexDirection: 'column',
              borderLeft: '1px solid #e0e0e0',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <FieldPropertiesPanel
                selectedField={getSelectedField()}
                selectedSection={getSelectedSection()}
                onFieldUpdate={handleFieldUpdate}
                onSectionUpdate={handleSectionUpdate}
                onFieldDelete={handleDeleteField}
              />
            </Box>
            <Box sx={{ flexBasis: '50%', borderTop: '1px solid #e0e0e0', overflow: 'auto' }}>
              <FieldListPanel
                fields={getFieldsForCurrentPage()}
                sections={(selectedForm?.formSections || []).filter((s: any) => {
                  try {
                    const cfg = typeof s.configurationJson === 'string'
                      ? JSON.parse(s.configurationJson)
                      : s.configurationJson;
                    return (cfg?.pageNumber || 1) === currentPage;
                  } catch {
                    return currentPage === 1;
                  }
                })}
                selectedFieldId={selectedFieldId}
                selectedSectionId={selectedSectionId}
                onFieldSelect={handleFieldSelect}
                onSectionSelect={handleSectionSelect}
                onAddField={handleAddField}
              />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Select a form to start editing
          </Typography>
        </Box>
      )}

      {/* SQL Generator Dialog */}
      {selectedForm && (
        <SqlGeneratorDialog
          open={sqlDialogOpen}
          formId={selectedForm.id}
          formDescription={selectedForm.description}
          onClose={() => setSqlDialogOpen(false)}
        />
      )}

      {/* Header Configuration Dialog */}
      {selectedForm && (
        <HeaderConfigDialog
          open={headerDialogOpen}
          headerConfig={selectedForm.headerConfigurationJson}
          onClose={() => setHeaderDialogOpen(false)}
          onSave={handleSaveHeaderConfig}
        />
      )}

      {/* Auto Layout Settings Dialog */}
      <AutoLayoutSettingsDialog
        open={autoLayoutSettingsOpen}
        settings={autoLayoutSettings}
        onClose={() => setAutoLayoutSettingsOpen(false)}
        onSave={setAutoLayoutSettings}
      />
    </Box>
  );
};
